/**
 * Shared document formatter (DOM-free).
 *
 * Verbatim port of the monolith's `formatDocHtml` — the text→legal-HTML/.docx
 * engine. Converts the structured plain-text produced by all build*Text()
 * functions into properly formatted legal HTML for print and .docx export.
 * Every recognition regex, branch order, CSS rule, and product string is
 * preserved exactly as in the original.
 *
 * Plain-text conventions recognised:
 *   CAPTION BLOCK   — lines that begin "COMMONWEALTH OF MASSACHUSETTS" trigger
 *                     a two-column caption table rendered per MA court style.
 *   ROMAN SECTIONS  — lines matching /^[IVX]+\.\s+[A-Z]/ → bold centred heading
 *   ALPHA SUB-HEADS — lines matching /^\s+[A-Z]\.\s+[A-Z]/ → indented bold h3
 *   NUMBERED PARAS  — lines matching /^\s+\d+\.\s/ → compact numbered paragraphs
 *   LETTERED ITEMS  — lines matching /^\s+[a-z]\.\s/ → indented list items
 *   BULLET ITEMS    — lines starting with •  → unordered list
 *   SIGNATURE BLOCK — line containing "/s/" → signature table
 *   NOTARY BLOCK    — "ACKNOWLEDGMENT" or "NOTARIZATION" keyword
 *   SECTION LABELS  — ALL-CAPS short lines ending in ":" → blue bar heading
 *   SOURCE:         — converted to clickable link
 *   AUTHORITY BLOCK — [CASE LAW], [STATUTE], [COURT RULE] → authority card
 *   DIVIDERS        — ═══ or ─── → rendered as horizontal rules
 */

export function formatDocHtml(title: string, text: string, forWord: boolean): string {
  const DATE_STR = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const css = [
    '@page{margin:1in 1.15in 1in 1.25in;}',
    '*{box-sizing:border-box;}',
    'body{font-family:"Times New Roman",Georgia,serif;font-size:12pt;line-height:2;color:#000;margin:0;background:#fff;}',
    // Cover
    '.cover{text-align:center;padding:18pt 0 14pt;border-bottom:3px double #1a3a6b;margin-bottom:16pt;}',
    '.cover-agency{font-size:8pt;font-weight:700;letter-spacing:1.8pt;text-transform:uppercase;color:#1a3a6b;margin-bottom:4pt;}',
    '.cover-title{font-size:17pt;font-weight:700;text-transform:uppercase;letter-spacing:0.3pt;margin-bottom:5pt;line-height:1.3;}',
    '.cover-date{font-size:9pt;color:#666;font-style:italic;}',
    // Caption (two-column court caption)
    '.caption-wrap{width:100%;margin-bottom:14pt;}',
    '.caption-table{width:100%;border-collapse:collapse;font-size:11.5pt;line-height:1.5;}',
    '.caption-left{width:55%;padding:2pt 4pt;vertical-align:top;}',
    '.caption-right{width:45%;padding:2pt 4pt 2pt 12pt;vertical-align:top;border-left:1.5pt solid #000;}',
    '.caption-court{font-size:10pt;font-weight:700;text-align:center;letter-spacing:0.5pt;text-transform:uppercase;padding:0 0 4pt;margin-bottom:6pt;border-bottom:1pt solid #000;}',
    '.caption-vs{font-style:italic;padding-left:24pt;}',
    '.caption-docket{font-size:10pt;}',
    '.caption-title{text-align:center;font-size:12pt;font-weight:700;text-transform:uppercase;letter-spacing:0.5pt;padding:10pt 0 8pt;border-top:1.5pt solid #000;border-bottom:1.5pt solid #000;margin:8pt 0 14pt;}',
    // Body text
    'p{margin:0 0 0 0;line-height:2;text-align:justify;}',
    '.para-block{margin-bottom:12pt;}',
    // Roman numeral headings
    '.roman-head{font-size:12pt;font-weight:700;text-align:center;text-transform:uppercase;margin:16pt 0 8pt;letter-spacing:0.3pt;}',
    // Alpha subheadings
    '.alpha-head{font-size:12pt;font-weight:700;margin:12pt 0 6pt 0;text-decoration:underline;}',
    // Numbered paragraphs (legal body)
    '.num-para{display:flex;gap:0;margin-bottom:10pt;line-height:2;}',
    '.num-label{min-width:36pt;font-weight:400;flex-shrink:0;padding-right:6pt;}',
    '.num-body{flex:1;text-align:justify;}',
    // Lettered sub-items
    '.let-para{display:flex;gap:0;margin:6pt 0 6pt 48pt;line-height:1.9;}',
    '.let-label{min-width:28pt;flex-shrink:0;}',
    '.let-body{flex:1;}',
    // Bullet items
    'ul.body-list{margin:6pt 0 10pt 36pt;padding:0;}',
    'ul.body-list li{margin-bottom:4pt;line-height:1.9;}',
    // Blue section bar
    'h2{font-size:11pt;font-weight:700;text-transform:uppercase;letter-spacing:0.7pt;color:#fff;background:#1a3a6b;padding:4pt 10pt;margin:18pt 0 0;-webkit-print-color-adjust:exact;print-color-adjust:exact;}',
    '.section-body{border:1px solid #c4d3e8;border-top:none;padding:10pt 13pt;margin-bottom:14pt;background:#fafcff;}',
    // Authority cards (law tab)
    '.authority{border:1px solid #c4d3e8;border-radius:3pt;margin-bottom:14pt;overflow:hidden;}',
    '.auth-header{padding:7pt 10pt;background:#dfe8f5;display:flex;gap:8pt;align-items:center;}',
    '.auth-badge{font-size:7.5pt;font-weight:700;text-transform:uppercase;letter-spacing:0.5pt;padding:2pt 7pt;border-radius:10pt;color:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact;}',
    '.auth-badge-case{background:#1a3a6b;}',
    '.auth-badge-stat{background:#065f46;}',
    '.auth-badge-rule{background:#4c1d95;}',
    '.auth-citation{font-size:11pt;font-weight:700;color:#1a3a6b;}',
    '.auth-body{padding:9pt 12pt;}',
    '.auth-summary{background:#eff6ff;border:1px solid #bfdbfe;border-radius:3pt;padding:7pt 10pt;margin-bottom:8pt;font-size:10.5pt;color:#1e3a5f;}',
    '.sub-label{font-size:8pt;font-weight:700;text-transform:uppercase;letter-spacing:0.7pt;color:#1a3a6b;margin:8pt 0 3pt;}',
    '.auth-fulltext{font-size:10.5pt;line-height:1.85;background:#f8fafc;border:1px solid #e2e8f0;border-radius:3pt;padding:8pt 10pt;margin-top:4pt;}',
    // Signature block
    '.sig-block{margin:20pt 0 14pt;page-break-inside:avoid;}',
    '.sig-intro{margin-bottom:14pt;line-height:2;}',
    '.sig-table{width:100%;border-collapse:collapse;margin-top:8pt;}',
    '.sig-left{width:60%;padding:0 12pt 0 0;vertical-align:bottom;}',
    '.sig-right{width:40%;padding:0;vertical-align:bottom;}',
    '.sig-line{border-top:1pt solid #000;margin-top:24pt;margin-bottom:3pt;}',
    '.sig-name{font-size:11pt;}',
    '.sig-title{font-size:10pt;color:#333;}',
    '.sig-bbo{font-size:10pt;color:#333;}',
    '.sig-addr{font-size:10pt;color:#333;white-space:pre-line;}',
    // Notary block
    '.notary-block{border:1pt solid #666;padding:12pt 14pt;margin:16pt 0;page-break-inside:avoid;}',
    '.notary-title{font-weight:700;text-align:center;margin-bottom:10pt;font-size:11pt;}',
    // Misc
    'mark,.hl{background:#fef08a;padding:0 2pt;-webkit-print-color-adjust:exact;print-color-adjust:exact;}',
    '.source-link{font-size:9pt;color:#1d4ed8;word-break:break-all;}',
    'ol{margin:6pt 0 10pt 20pt;padding:0;}',
    'ol li{margin-bottom:4pt;line-height:1.9;}',
    'hr.div-heavy{border:none;border-top:2pt solid #1a3a6b;margin:14pt 0;}',
    'hr.div-light{border:none;border-top:1pt solid #888;margin:10pt 0;}',
    '.footer{margin-top:24pt;border-top:1pt solid #c4d3e8;padding-top:5pt;font-size:8pt;color:#888;text-align:center;}',
    '@media print{body{margin:0;}h2{background:#222!important;}.auth-badge{-webkit-print-color-adjust:exact;print-color-adjust:exact;}a{color:#000;text-decoration:none;}}'
  ].join('');

  function esc(s: string): string { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

  const lines = text.split('\n');
  let html = '';
  let i = 0;

  // ── Cover block ──────────────────────────────────────────────────────────
  html += '<div class="cover">'
    + '<div class="cover-agency">Commonwealth of Massachusetts &mdash; SpatchAI Jury Intelligence</div>'
    + '<div class="cover-title">' + esc(title) + '</div>'
    + '<div class="cover-date">Generated: ' + DATE_STR + '</div>'
    + '</div>';

  // ── State machine ────────────────────────────────────────────────────────
  let inSection = false;   // inside a blue-bar .section-body
  let inAuthority = false;   // inside an authority card
  const captionMode = false;   // collecting caption lines
  const captionLines: string[] = [];
  const docTitleLines: string[] = [];    // motion/affidavit title lines collected after caption

  function closeSection(): string {
    let out = '';
    if (inSection) { out += '</div>'; inSection = false; }
    return out;
  }
  function openSection(label: string): string {
    let out = closeSection();
    out += '<h2>' + esc(label) + '</h2><div class="section-body">';
    inSection = true;
    return out;
  }
  function closeAuthority(): string {
    let out = '';
    if (inAuthority) { out += '</div></div>'; inAuthority = false; }
    return out;
  }

  // ── Render caption block as proper two-column court caption ───────────────
  function renderCaption(capLines: string[]): string {
    // Parse out: court, parties, docket, "v." separator
    let court = '';
    let plaintiff = '';
    let defendant = '';
    let docket = '';
    let vs = false;
    capLines.forEach(function (l: string) {
      const t = l.trim();
      if (!t) return;
      if (/^COMMONWEALTH OF MASSACHUSETTS$/i.test(t) && !court) { court = t; return; }
      if (/SUPERIOR COURT|DISTRICT COURT|MUNICIPAL COURT|JUVENILE COURT|APPEALS COURT|SUPREME JUDICIAL/i.test(t)) { court = t; return; }
      if (/^\bCOMMONWEALTH OF MASSACHUSETTS,?\s*\)/.test(t)) { plaintiff = 'COMMONWEALTH OF MASSACHUSETTS'; return; }
      if (/^\s*Plaintiff,?/.test(t)) return;
      if (/Docket No\.?\s*(.+?)\s*\)/.test(t)) { const m = t.match(/Docket No\.?\s*([^)]+)/); if (m) docket = m[1].trim(); return; }
      if (/^\s*v\.\s*\)/.test(t) || /^\s*v\.\s*$/.test(t)) { vs = true; return; }
      if (vs && !defendant && /,?\s*\)/.test(t)) { defendant = t.replace(/,?\s*\).*$/, '').trim(); return; }
      if (/Defendant\.?/.test(t)) return;
    });
    let out = '<div class="caption-wrap">';
    out += '<div class="caption-court">' + esc(court || 'Commonwealth of Massachusetts') + '</div>';
    out += '<table class="caption-table"><tr>';
    out += '<td class="caption-left">';
    out += '<p>' + esc(plaintiff || 'COMMONWEALTH OF MASSACHUSETTS') + ',</p>';
    out += '<p style="padding-left:36pt;">Plaintiff,</p><br>';
    out += '<p class="caption-vs">v.</p><br>';
    out += '<p>' + esc(defendant || '_______________________') + ',</p>';
    out += '<p style="padding-left:36pt;">Defendant.</p>';
    out += '</td>';
    out += '<td class="caption-right">';
    if (docket) out += '<p class="caption-docket">Docket No. ' + esc(docket) + '</p>';
    out += '</td>';
    out += '</tr></table></div>';
    return out;
  }

  // ── Render signature block ────────────────────────────────────────────────
  // Triggered by a line containing "/s/" or a block of ___ lines + name + title
  function renderSigBlock(sigLines: string[]): string {
    // eslint-disable-next-line prefer-const -- verbatim formatDocHtml locals (some void-discarded)
    let intro = '', name = '', title = '', bbo = '', addr: string[] = [], esig = '';
    void intro;
    sigLines.forEach(function (l: string) {
      const t = l.trim();
      if (/^\/s\//i.test(t)) { esig = t.replace(/^\/s\/\s*/i, ''); return; }
      if (/^_{10,}/.test(t)) return; // signature underline — rendered as .sig-line
      if (/^BBO No/i.test(t)) { bbo = t; return; }
      if (/Attorney|Counsel|District|Defendant|Plaintiff/i.test(t) && !title) { title = t; return; }
      if (!name && t && !/^Date/i.test(t) && !/^My commission/i.test(t)) { name = t; return; }
      if (t) addr.push(t);
    });
    let out = '<div class="sig-block">';
    if (esig) out += '<p style="margin-bottom:6pt;"><em>Electronically signed: /s/ ' + esc(esig) + '</em></p>';
    out += '<table class="sig-table"><tr>';
    out += '<td class="sig-left"><div class="sig-line"></div>';
    if (name) out += '<div class="sig-name">' + esc(name) + '</div>';
    if (title) out += '<div class="sig-title">' + esc(title) + '</div>';
    if (bbo) out += '<div class="sig-bbo">' + esc(bbo) + '</div>';
    if (addr.length) out += '<div class="sig-addr">' + addr.map(esc).join('\n') + '</div>';
    out += '</td>';
    out += '<td class="sig-right"><div class="sig-line"></div><div class="sig-title">Date</div></td>';
    out += '</tr></table></div>';
    return out;
  }

  // ── Main parser loop ─────────────────────────────────────────────────────
  let captureCaption = false;
  let captionBuf: string[] = [];
  let captionDone = false;
  const captionTitleBuf: string[] = [];
  const captureCaptionTitle = false;
  let inSigBlock = false;
  let sigBuf: string[] = [];
  let inNotary = false;
  let notaryBuf: string[] = [];
  let bulletBuf: string[] = [];

  void [captionMode, captionLines, docTitleLines, captureCaptionTitle];

  function flushBullets(): string {
    if (!bulletBuf.length) return '';
    const out = '<ul class="body-list">' + bulletBuf.map(function (b: string) { return '<li>' + esc(b) + '</li>'; }).join('') + '</ul>';
    bulletBuf = [];
    return out;
  }

  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trim();
    i++;

    if (!line) {
      // blank line — flush bullets, end sig/notary blocks
      if (inSigBlock) {
        html += renderSigBlock(sigBuf);
        inSigBlock = false; sigBuf = [];
      }
      if (inNotary && notaryBuf.length > 3) {
        // render notary
        html += '<div class="notary-block"><div class="notary-title">ACKNOWLEDGMENT / NOTARIZATION</div>';
        notaryBuf.forEach(function (nl: string) {
          const nt = nl.trim();
          if (nt) html += '<p style="line-height:1.9;margin-bottom:4pt;">' + esc(nt) + '</p>';
        });
        html += '</div>';
        inNotary = false; notaryBuf = [];
      }
      html += flushBullets();
      continue;
    }

    // ── Caption capture ──────────────────────────────────────────────────────
    // Collect caption lines until a divider terminates it. Checked BEFORE the
    // divider branches so the terminating divider ends the caption rather than
    // being consumed as a standalone divider (bug fix vs. the monolith, where
    // captioned documents silently lost their entire body on print/export).
    if (captureCaption) {
      if (/^[=═]{4,}/.test(line) || /^[-─―]{4,}/.test(line)) {
        html += renderCaption(captionBuf);
        captionDone = true; captureCaption = false; captionBuf = [];
        html += '<hr class="div-light">';
        continue;
      }
      captionBuf.push(raw);
      continue;
    }

    // ── Heavy divider ───────────────────────────────────────────────────────
    if (/^[=═]{4,}/.test(line)) {
      if (inSigBlock) { html += renderSigBlock(sigBuf); inSigBlock = false; sigBuf = []; }
      html += closeAuthority() + closeSection() + '<hr class="div-heavy">';
      captureCaption = false; captionDone = false;
      continue;
    }
    // ── Light divider ────────────────────────────────────────────────────────
    if (/^[-─―]{4,}/.test(line)) {
      if (inSigBlock) { html += renderSigBlock(sigBuf); inSigBlock = false; sigBuf = []; }
      html += closeAuthority() + '<hr class="div-light">';
      continue;
    }

    // ── Caption trigger ──────────────────────────────────────────────────────
    if (/^COMMONWEALTH OF MASSACHUSETTS\s*$/.test(line) && !captionDone) {
      captureCaption = true;
      captionBuf = [line];
      continue;
    }

    // ── Document title (centred, after caption, before first section) ────────
    // Lines that are all-caps and centred — become the .caption-title block
    if (captionDone && !inSection && !inAuthority &&
      line === line.toUpperCase() && line.length > 6 && line.length < 120 &&
      !/^[IVX]+\.\s/.test(line) && !/^[A-Z]\.\s/.test(line) &&
      !/^SOURCE:/i.test(line) && !/NOW COMES/.test(line) &&
      !/SIGNED/.test(line) && !/WHEREFORE/.test(line) &&
      !captionTitleBuf.length && !/Trial Date/.test(line) && !/Next Court Date/.test(line) &&
      !/Next Event Date/.test(line) && !/Status Date/.test(line)) {
      html += '<div class="caption-title">' + esc(line) + '</div>';
      continue;
    }

    // ── "NOW COMES" / introductory paragraph ────────────────────────────────
    if (/^NOW COMES\b/i.test(line)) {
      html += flushBullets() + '<p class="para-block">' + esc(line) + '</p>';
      continue;
    }

    // ── Roman numeral section headings (I. II. III. etc.) ───────────────────
    if (/^[IVX]+\.\s+\S/.test(line)) {
      html += flushBullets() + closeSection() + closeAuthority();
      html += '<div class="roman-head">' + esc(line) + '</div>';
      continue;
    }

    // ── Alpha subheadings (A. B. C. with mixed case label) ──────────────────
    if (/^[A-Z]\.\s+[A-Z][a-z]/.test(line) && line.length < 100) {
      html += flushBullets();
      html += '<div class="alpha-head">' + esc(line) + '</div>';
      continue;
    }

    // ── Notary / Acknowledgment block ───────────────────────────────────────
    if (/ACKNOWLEDGMENT|NOTARIZATION|NOTARY|COUNTY OF/i.test(line) && !inNotary) {
      html += flushBullets();
      inNotary = true;
      notaryBuf = [raw];
      continue;
    }
    if (inNotary) {
      notaryBuf.push(raw);
      continue;
    }

    // ── Signature trigger: /s/ or line of underscores followed by name ───────
    if (/^\/s\//i.test(line) || (/_{10,}/.test(line) && !inSigBlock)) {
      if (!inSigBlock) { inSigBlock = true; sigBuf = []; }
      sigBuf.push(raw);
      continue;
    }
    if (inSigBlock) {
      // Keep collecting sig lines until blank line ends the block
      sigBuf.push(raw);
      continue;
    }

    // ── Authority block [CASE LAW], [STATUTE], [COURT RULE] ─────────────────
    const authMatch = line.match(/^\[([A-Z][A-Z ]+)\]\s*(.+)/);
    if (authMatch) {
      html += flushBullets() + closeSection() + closeAuthority();
      const atype = authMatch[1].toLowerCase();
      const badgeC = atype.indexOf('statute') > -1 ? 'auth-badge auth-badge-stat'
        : atype.indexOf('rule') > -1 ? 'auth-badge auth-badge-rule'
          : 'auth-badge auth-badge-case';
      html += '<div class="authority"><div class="auth-header"><span class="' + badgeC + '">' + esc(authMatch[1]) + '</span><span class="auth-citation">' + esc(authMatch[2]) + '</span></div><div class="auth-body">';
      inAuthority = true;

      // Parse authority sub-sections inline
      let authContent = '';
      while (i < lines.length) {
        const al = lines[i].trim();
        if (/^[=═]{4,}/.test(al) || /^\[[A-Z]/.test(al)) break;
        if (/^SUMMARY:?$/.test(al)) {
          i++;
          authContent += '<div class="sub-label">Summary</div><div class="auth-summary">';
          while (i < lines.length) {
            const asl = lines[i].trim();
            if (!asl || /^[A-Z]{3,}.*:?$/.test(asl) || /^[=\-]{4,}/.test(asl) || /^\[/.test(asl)) break;
            authContent += esc(asl) + ' '; i++;
          }
          authContent += '</div>'; continue;
        }
        if (/^KEY HOLDINGS:?$/.test(al)) {
          i++;
          authContent += '<div class="sub-label">Key Holdings</div><ol>';
          while (i < lines.length) {
            const ahl = lines[i].trim();
            if (!ahl || /^[A-Z]{3,}/.test(ahl) || /^[=\-]{4,}/.test(ahl) || /^\[/.test(ahl)) break;
            const ahm = ahl.match(/^\d+[.)]\s*(.+)/);
            if (ahm) { authContent += '<li>' + esc(ahm[1]) + '</li>'; i++; } else break;
          }
          authContent += '</ol>'; continue;
        }
        if (/^FULL TEXT:?$/.test(al)) {
          i++;
          authContent += '<div class="sub-label">Full Text</div><div class="auth-fulltext">';
          while (i < lines.length) {
            if (/^[=\-]{4,}/.test(lines[i].trim()) || /^\[/.test(lines[i].trim())) break;
            authContent += esc(lines[i]) + '<br>'; i++;
          }
          authContent += '</div>'; continue;
        }
        if (/^SOURCE:/i.test(al)) {
          const su = al.replace(/^SOURCE:\s*/i, '');
          authContent += '<div class="sub-label">Source</div><p><a class="source-link" href="' + esc(su) + '" target="_blank">' + esc(su) + '</a></p>';
          i++; continue;
        }
        if (al) authContent += '<p>' + esc(al) + '</p>';
        i++;
      }
      html += authContent + '</div></div>';
      inAuthority = false;
      continue;
    }

    // ── SOURCE: link (outside authority) ─────────────────────────────────────
    if (/^SOURCE:/i.test(line)) {
      const url2 = line.replace(/^SOURCE:\s*/i, '');
      html += '<p><a class="source-link" href="' + esc(url2) + '" target="_blank">' + esc(url2) + '</a></p>';
      continue;
    }

    // ── Bullet items ─────────────────────────────────────────────────────────
    if (/^[•·\-]\s/.test(line) || /^•/.test(line)) {
      const btext = line.replace(/^[•·\-•]\s*/, '');
      bulletBuf.push(btext);
      continue;
    }

    // ── Numbered paragraphs (legal body:  "    1.  text" or "  1.  text") ────
    const numPara = raw.match(/^(\s{1,8})(\d+)\.\s{1,4}(.+)/);
    if (numPara && parseInt(numPara[2], 10) < 200) {
      html += flushBullets();
      html += '<div class="num-para"><div class="num-label">' + esc(numPara[2]) + '.</div><div class="num-body">' + esc(numPara[3].trim()) + '</div></div>';
      continue;
    }

    // ── Lettered sub-items ("    a.  text" or "        a.  text") ───────────
    const letPara = raw.match(/^(\s{4,12})([a-z])\.\s{1,4}(.+)/);
    if (letPara) {
      html += flushBullets();
      html += '<div class="let-para"><div class="let-label">(' + esc(letPara[2]) + ')</div><div class="let-body">' + esc(letPara[3].trim()) + '</div></div>';
      continue;
    }

    // ── Blue bar section heading (ALL-CAPS line ending in ":") ───────────────
    const barMatch = line.match(/^([A-Z][A-Z0-9 ,\/&#\.\-]{2,}):$/);
    if (barMatch && line.length < 72 && !/^COUNTY OF/.test(line) &&
      !/^COMMONWEALTH/.test(line) && !/^SIGNED/.test(line)) {
      html += flushBullets() + closeAuthority() + openSection(barMatch[1]);
      continue;
    }

    // ── Case meta line (e.g. "Next Event Date: ... Presiding Justice: ...") ──
    // The date line has been worded three ways: "Trial Date" originally, then
    // "Next Court Date", now "Status Date" / "Next Event Date" — the two dates
    // MassCourts actually publishes. All are matched, because documents saved
    // under an earlier wording must keep laying out correctly.
    if (
      /Status Date:/i.test(line) ||
      /Next Event Date:/i.test(line) ||
      /Next Court Date:/i.test(line) ||
      /Trial Date:/i.test(line) ||
      /Presiding Justice:/i.test(line) ||
      /Presiding:/i.test(line)
    ) {
      html += '<p style="margin:8pt 0;font-size:11pt;">' + esc(line) + '</p>';
      continue;
    }

    // ── "SIGNED AND SWORN" / "Signed under" intro line ──────────────────────
    if (/^SIGNED (AND SWORN|UNDER)/i.test(line) || /^Signed under/i.test(line)) {
      html += '<p style="margin:14pt 0 4pt;font-weight:700;">' + esc(line) + '</p>';
      continue;
    }

    // ── "Respectfully submitted" / closing ───────────────────────────────────
    if (/^Respectfully submitted/i.test(line) || /^WHEREFORE/i.test(line)) {
      html += '<p style="margin:14pt 0 4pt;">' + esc(line) + '</p>';
      continue;
    }

    // ── Generic line → paragraph ─────────────────────────────────────────────
    html += flushBullets() + '<p>' + esc(line) + '</p>';
  }

  // flush any remaining
  if (inSigBlock && sigBuf.length) html += renderSigBlock(sigBuf);
  if (inNotary && notaryBuf.length) {
    html += '<div class="notary-block"><div class="notary-title">ACKNOWLEDGMENT / NOTARIZATION</div>';
    notaryBuf.forEach(function (nl: string) {
      const nt = nl.trim();
      if (nt) html += '<p style="line-height:1.9;margin-bottom:4pt;">' + esc(nt) + '</p>';
    });
    html += '</div>';
  }
  html += flushBullets() + closeSection() + closeAuthority();
  html += '<div class="footer">SpatchAI Jury Intelligence &bull; Confidential Attorney Work Product &bull; ' + DATE_STR + '</div>';

  const ns = forWord
    ? 'xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"'
    : '';
  return '<!DOCTYPE html><html ' + ns + '><head><meta charset="utf-8"><title>' + esc(title) + '</title><style>' + css + '</style></head><body>' + html + '</body></html>';
}
